import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

type HelpCategory = 'about' | 'tenant' | 'owner' | null;

const helpCards: { key: HelpCategory; label: string; description: string; icon: string }[] = [
  {
    key: 'about',
    label: 'About Us',
    description: 'Understand how SettleSpace works',
    icon: 'ri-building-2-line',
  },
  {
    key: 'tenant',
    label: 'Tenant',
    description: 'Questions about finding and renting a home',
    icon: 'ri-search-line',
  },
  {
    key: 'owner',
    label: 'Owner',
    description: 'Questions about renting out your home',
    icon: 'ri-home-4-line',
  },
];

type FaqItem = { q: string; a: string };

const aboutFaqItems: FaqItem[] = [
  {
    q: 'Which locations does SettleSpace currently serve?',
    a: 'SettleSpace primarily serves Kharghar and also assists customers across CBD Belapur, Seawoods and Nerul.',
  },
  {
    q: 'What type of properties does SettleSpace handle?',
    a: 'SettleSpace currently focuses on residential rentals only.\n\nWe do not assist with property sales or commercial properties. PG accommodation may also be considered depending on the customer\u2019s requirement and availability.',
  },
  {
    q: 'What are SettleSpace\u2019s working hours?',
    a: 'SettleSpace is available every day from 9:00 a.m. to 10:00 p.m.',
  },
  {
    q: 'Does SettleSpace have a physical office?',
    a: 'SettleSpace currently operates without a physical office because our services can be managed effectively through phone calls, WhatsApp, email and other digital communication.\n\nTenants and property owners do not need to travel to an office to speak with us. Where a physical meeting becomes necessary for a property visit, documentation, biometric process or another specific requirement, it can be coordinated accordingly.',
  },
  {
    q: 'Can the complete rental process be handled online?',
    a: 'Most of the rental process can be handled online through calls, WhatsApp and digital coordination.\n\nFor the leave-and-licence agreement, you do not need to travel to an office. The biometric process is completed through an authorised service provider who can visit a location convenient for you.\n\nPhysical presence may still be required for property visits, handover or any step that specifically needs in-person verification.',
  },
  {
    q: 'How is SettleSpace different from property-listing websites?',
    a: 'We focus on providing tenants and owners with accurate, current and verified information on houses.\n\nWe do not publish property listings directly on our website or rely on them as they may remain visible even though the house has already been rented or is no longer available. Instead, we communicate with the relevant parties, confirm the latest status and share suitable options based on the customer\u2019s actual requirement.',
  },
  {
    q: 'Does submitting an enquiry guarantee that a suitable property will be found?',
    a: 'If your requirement matches a property currently available through SettleSpace, we can proceed with that option subject to availability, owner approval and completion of the required checks.\n\nIf a suitable property is not immediately available, we will begin searching for options that closely match your location, budget and other requirements.',
  },
  {
    q: 'How quickly does SettleSpace respond to an enquiry?',
    a: 'During our working hours, we aim to respond as quickly as possible and usually attend to new enquiries almost immediately.\n\nResponse times may vary depending on availability and the nature of the enquiry.',
  },
  {
    q: 'Can SettleSpace decline an enquiry or discontinue service?',
    a: 'We strive to assist everyone fairly and do not discriminate on the basis of caste, creed, religion, color or similar personal characteristics.\n\nHowever, we may refuse or discontinue service if a person provides false information, behaves inappropriately, attempts to misuse the service, engages in unlawful activity, or acts in breach of our Terms and Conditions.',
  },
];

const ownerFaqItems: FaqItem[] = [
  {
    q: 'Is full responsibility taken for the keys in order to showcase the house?',
    a: 'Yes. SettleSpace takes full responsibility for the keys entrusted to us and keeps track of their whereabouts. Keys are not duplicated, misused or used to visit the property without a valid reason or the owner\u2019s knowledge and permission.',
  },
  {
    q: 'Is an acknowledgement provided as proof for handing over the keys?',
    a: 'Yes. A formal written and signed acknowledgement is provided, either physically or online. This serves as a record of responsibility and keeps track of the number of keys received.',
  },
  {
    q: 'Can the keys be collected and taken back anytime?',
    a: 'Yes. You may collect your keys whenever necessary and hand them back to us whenever you require our assistance in showcasing the property. If a visit has already been scheduled, the keys will need to be provided to us beforehand.',
  },
  {
    q: 'Is it ensured that the house, windows, doors and main switches are turned off and locked after showing the house?',
    a: 'Yes. We conduct a thorough check before leaving the property, ensuring that the required switches are turned off and that windows and doors are properly secured. We also do not allow prospective clients to use the restrooms, helping us maintain the property\u2019s cleanliness and hygiene.',
  },
  {
    q: 'Is the booking amount to be refunded in any case?',
    a: 'A booking amount does not have to be refunded in every situation. However, where there is a clear discrepancy or a problem to fulfil the agreed terms, the booking amount must be refunded in accordance with the circumstances.',
  },
  {
    q: 'How long does the agreement process take?',
    a: 'The agreement process usually takes 3 to 7 working days from start to finish. We always work to complete it as quickly as possible. In some cases, delays may occur outside our control due to government or sub-registrar portal congestion, server issues or other technical difficulties.',
  },
  {
    q: 'Can changes be made to the agreement after it is submitted?',
    a: 'Yes, minor changes can be made where required. Once the agreement has been submitted and received through the online registration portal, we can make the necessary alterations.',
  },
  {
    q: 'How does payment for the agreement work?',
    a: 'The agreement cost is shared equally between the tenant and the owner. The total amount depends on factors such as the rent, security deposit and tenure agreed upon.',
  },
  {
    q: 'What are shifting charges and who must pay them, if applicable?',
    a: 'The one-time shifting charge is paid by the tenant directly to the society and only applies where the society requires it.',
  },
  {
    q: 'What documents are required for the agreement?',
    a: 'For the necessary documentation, owners are generally required to provide:\n\n\u2022 Aadhaar Card \u2014 both sides\n\u2022 PAN Card\n\nAdditional documents may be requested wherever necessary.',
  },
  {
    q: 'What are the basic fittings to be provided to the tenant?',
    a: 'We recommend providing essential fittings such as fans, lights, exhaust fans, curtain rods, mirrors and towel hangers. These practical fittings make the home more appealing to prospective tenants and can help it attract tenants more quickly.\n\nAdditional fittings such as invisible grills or mosquito nets may also be provided if agreed upon. These additions may involve an additional cost or adjustment to the rent.',
  },
  {
    q: 'What other charges are to be paid apart from the service fee?',
    a: 'Your basic cleaning and pest control are arranged and paid for by SettleSpace as part of our service.\n\nOther applicable costs are the agreement charges and the SettleSpace service fee.',
  },
  {
    q: 'What is the time range of the notice period issued to vacate?',
    a: 'The standard notice period is 45 days from either side before vacating. This may be reduced to one month upon mutual agreement between the owner and tenant.',
  },
  {
    q: 'Will basic cleaning and pest control be paid for and done before shifting?',
    a: 'Yes. Both these essentials are part of our service and will be arranged and paid for by SettleSpace before the tenant moves in.',
  },
  {
    q: 'At what stage are the service charges supposed to be paid?',
    a: 'Once the documentation process has been completed, including the submitted agreement, police verification and society NOC where applicable, you can proceed with clearing the SettleSpace service charges.',
  },
  {
    q: 'Is personal information and data kept confidential with you?',
    a: 'Yes, absolutely. We value personal information and do not share it without permission. Where necessary, information may only be shared with the relevant parties involved in the rental process.',
  },
  {
    q: 'Do you assist with queries even after shifting is done?',
    a: 'Yes, certainly. Our service does not end once the house is rented. We continue to assist where necessary to ensure that the transition after shifting is smooth and that any concerns are addressed appropriately.',
  },
  {
    q: 'What documents are required to verify ownership?',
    a: 'Depending on the property, documents such as Index 2, electricity bills and possession or parking letters may be requested to verify ownership and the relevant property details. Additional documents may be requested where necessary.',
  },
  {
    q: 'Is the full documentation and verification process handled by SettleSpace?',
    a: 'Yes. SettleSpace handles the documentation, verification and other necessary processes on your behalf. For the rental agreement and police verification, SettleSpace coordinates with and engages a third-party company or individual to carry out the required processes. We reach out whenever your input is required and keep you informed throughout the process so you know what stage everything is at.',
  },
  {
    q: 'Who receives the booking amount?',
    a: 'The booking amount is paid directly to the owner once the rent, deposit and other formalities have been finalized and the owner\u2019s account details have been received. SettleSpace does not collect or hold the booking amount. We do, however, keep track of the transaction to ensure that the payment has been completed and properly recorded.',
  },
  {
    q: 'Is the house inspected for any breaks and leaks at the end of tenancy?',
    a: 'Yes. Our commitment extends through the end of the tenancy. A full inspection of the house is conducted on video during the handover process and shared with the relevant parties to document the condition of the property and identify any changes, damages, breakages or leaks.',
  },
  {
    q: 'Must the deposit amount be returned before or after vacating?',
    a: 'The security deposit is generally settled during the vacating process on the last day, after the house has been inspected and the handover has been completed. The inspection helps identify any outstanding damage, breakages, leaks or other issues that may need to be addressed in accordance with the agreement.',
  },
  {
    q: 'Will I receive unnecessary promotional messages or calls?',
    a: 'No. SettleSpace does not send unnecessary promotional messages or calls. We only contact you regarding your property, rental enquiry, existing service, or other information relevant to your interaction with us.',
  },
];

const tenantFaqItems: FaqItem[] = [
  {
    q: 'Is the booking amount refundable?',
    a: 'In certain circumstances if the owner is unable to provide the property, fittings, amenities or other necessities that were agreed upon at the time of booking.\n\nIf new requirements are introduced that were not previously agreed upon and the owner is unable to fulfil them, the owner may choose whether to refund the booking amount.',
  },
  {
    q: 'Is the booking amount included as part of the rent/deposit?',
    a: 'Yes. The booking amount can be adjusted against either the first month\u2019s rent or the security deposit, depending on the arrangement.',
  },
  {
    q: 'How long does the agreement process take?',
    a: 'The agreement process usually takes 3 to 7 working days from start to finish. We always work to complete it as quickly as possible. In some cases, delays may occur outside our control due to government or sub-registrar portal congestion, server issues, or other technical difficulties.',
  },
  {
    q: 'Will shifting be given on time?',
    a: 'Yes. The agreement process is planned with sufficient time before the scheduled shifting date and work to ensure everything is completed on time. We also keep you updated throughout the process and make every effort to avoid delays from our end.',
  },
  {
    q: 'Is it possible to change the shifting date?',
    a: 'Yes. The shifting date can be modified if required, subject to the agreed terms. If the move-in date is extended after the date specified in the agreement, rent will generally continue to be calculated from the agreed commencement date unless the owner and tenant mutually agree to a newly revised date.',
  },
  {
    q: 'Can changes be made to the agreement after it is submitted?',
    a: 'Yes, minor changes can be made where required. Once the agreement has been submitted and received through the online registration process, we can make the necessary alterations and proceed accordingly.',
  },
  {
    q: 'How does payment for the agreement work?',
    a: 'The agreement cost is shared equally between the tenant and the owner. The total amount depends on factors such as the rent, security deposit and tenure agreed upon.',
  },
  {
    q: 'What are shifting charges and who must pay them, if applicable?',
    a: 'In many apartment societies, a one-time shifting charge may be applicable to the tenant. This generally covers the use and maintenance of the service lift and other arrangements made by the society for moving furniture and belongings. The payment is made by the tenant directly to the society, not to the owner, and only applies where the society requires it.',
  },
  {
    q: 'How is the rent and deposit to be paid?',
    a: 'For convenient and reliable record-keeping, we prefer online bank transfers or UPI payments. A cheque may also be used where required.',
  },
  {
    q: 'Is maintenance included in the rent?',
    a: 'Yes. Regular maintenance of the house is generally included in the rent and is not charged separately, unless otherwise agreed upon.',
  },
  {
    q: 'What documents are required for the agreement?',
    a: 'For the necessary documentation, tenants are generally required to provide:\n\n\u2022 Aadhaar Card \u2014 both sides\n\u2022 PAN Card\n\u2022 Work ID, if employed\n\nAdditional documents may be requested where necessary depending on the agreement or property requirements.',
  },
  {
    q: 'What are the basic fittings provided by the owner?',
    a: 'We encourage owners to provide essential fittings such as fans, lights, exhaust fans, curtain rods, mirrors and towel hangers.\n\nAdditional fittings such as invisible grills or mosquito nets may also be provided if agreed upon with the owner. Depending on the arrangement, these additions may involve an additional cost or adjustment to the rent.',
  },
  {
    q: 'Are the amenities free or paid to use?',
    a: 'Major common amenities such as walking areas, children\u2019s play areas, swimming pools and outdoor sports areas are generally free to use, although an access card may be required.\n\nIndoor facilities such as badminton courts, squash courts, lounges or ceremony halls, where available, may involve additional charges.',
  },
  {
    q: 'What other charges are to be paid apart from rent and deposit?',
    a: 'Your basic cleaning and pest control are arranged and paid for by SettleSpace as part of our service.\n\nOther costs that may apply are the agreement charges, shifting charges where applicable, and the SettleSpace service fee.',
  },
  {
    q: 'Is the electricity/gas bill to be paid to the owner or the provider?',
    a: 'We preferably recommend paying utility bills directly to the respective provider. In certain situations, you may instead need to pay the owner, who will make the payment on your behalf.',
  },
  {
    q: 'Are there any shifting timings?',
    a: 'Yes, shifting timings mostly apply, particularly in apartment societies. Most societies have designated hours during which furniture and belongings can be moved. Extensions may be requested when necessary, subject to the society\u2019s approval.\n\nFor standalone houses, specific shifting timings may not apply.',
  },
  {
    q: 'What is the time range of the notice period in order to vacate?',
    a: 'The standard notice period is 45 days from either side before vacating. This may even be reduced to one month upon mutual agreement between the owner and tenant.',
  },
  {
    q: 'Will basic cleaning and pest control be paid for and done before shifting?',
    a: 'Yes. Both of these essentials are part of our service and will be arranged and paid for by SettleSpace before your move in.',
  },
  {
    q: 'At what stage are the service charges supposed to be paid?',
    a: 'Once the documentation process has been completed, including the submitted agreement, police verification and society NOC where applicable, you can proceed with clearing the SettleSpace service charges.',
  },
  {
    q: 'Is personal information and data kept confidential with you?',
    a: 'Yes, absolutely. We value your personal information and do not share it without permission. Where necessary, information may only be shared with the relevant parties involved in the rental process.',
  },
  {
    q: 'Do you assist with queries even after moving in?',
    a: 'Yes, certainly. Our work does not end once the house is rented. If any repairs, fixes or other agreed work are still pending after you move in, SettleSpace will make sure they are completed as soon as possible. We want to make sure you are well settled, comfortable and supported, and are not facing difficulties after moving in.',
  },
  {
    q: 'Are the ownership documents of the house verified before renting it?',
    a: 'Yes. All homes we place for rent undergo ownership verification and cross-checking with the owner to help ensure a transparent and reliable rental process.',
  },
  {
    q: 'Is the full documentation and verification process handled by SettleSpace?',
    a: 'Yes. SettleSpace handles the documentation, verification and other necessary processes on your behalf. For the rental agreement and police verification, SettleSpace coordinates with and engages a third-party company or individual to carry out the required processes. We reach out whenever your input is required and keep you informed throughout the process so you know what stage everything is at.',
  },
  {
    q: 'Who is the booking amount paid to?',
    a: 'It is paid directly to the owner after their account details are received and once the rent, deposit and other formalities have been finalized. SettleSpace does not collect or hold the booking amount. We do, however, keep track of the transaction to ensure that the payment has been completed and properly recorded.',
  },
  {
    q: 'When are the keys handed over and how many sets of them?',
    a: 'Keys are handed over to you once the documentation and police verification process has been completed in full and the SettleSpace service charges have been cleared. Typically, 2 to 3 sets of keys are provided, depending on the BHK configuration and the number of rooms.',
  },
  {
    q: 'Is the deposit amount transferred before or after vacating?',
    a: 'The security deposit is generally settled during the vacating process on the last day, after the house has been inspected and the handover has been completed. The inspection helps identify any outstanding damage, breakages, leaks or other issues that may need to be addressed in accordance with the agreement.',
  },
  {
    q: 'Will I receive unnecessary promotional messages or calls?',
    a: 'No. SettleSpace does not send unnecessary promotional messages or calls. We only contact you regarding your property, rental enquiry, existing service, or other information relevant to your interaction with us.',
  },
];

function FaqModal({
  open,
  onClose,
  title,
  items,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  items: FaqItem[];
}) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const expandTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setOpenFaq(null);
  }, [open, items]);

  useEffect(() => {
    return () => {
      if (expandTimer.current) window.clearTimeout(expandTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    // On mobile, a plain overflow:hidden does NOT stop the background from
    // rubber-banding. Freeze the body in place instead so the page behind
    // the card stays completely static while it is open.
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const body = document.body;

    if (!isMobile) {
      body.style.overflow = 'hidden';
      return () => {
        body.style.overflow = '';
      };
    }

    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const toggleFaq = (index: number) => {
    setOpenFaq((prev) => {
      const next = prev === index ? null : index;
      if (next !== null) {
        if (expandTimer.current) window.clearTimeout(expandTimer.current);
        expandTimer.current = window.setTimeout(() => {
          const container = scrollRef.current;
          if (!container) return;
          const target = container.querySelector(`[data-faq-index="${index}"]`);
          target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 560);
      }
      return next;
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-6 transition-opacity duration-300 ease-out ${
        open ? 'opacity-100' : 'opacity-0 pointer-events-none invisible'
      }`}
    >
      {/* backdrop */}
      <div
        className={`absolute inset-0 bg-foreground-950/40 backdrop-blur-sm transition-opacity duration-300 ease-out ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* modal panel */}
      <div
        className={`relative w-full max-w-xl max-h-[85vh] bg-background-50 rounded-[32px] overflow-hidden flex flex-col transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-6 scale-[0.97] opacity-0'
        }`}
      >
        {/* Header — fixed at top */}
        <div className="flex-shrink-0 px-7 pt-7 pb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg md:text-xl font-light tracking-wide text-foreground-950">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Scrollable FAQ list */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain px-7 pb-7">
          <div className="flex flex-col">
            {items.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  data-faq-index={i}
                  className={`border-b border-foreground-200/60 last:border-b-0 transition-colors duration-300 ${
                    isOpen ? 'bg-background-100/40 rounded-2xl border-transparent' : ''
                  }`}
                >
                  {/* Question row */}
                  <button
                    type="button"
                    onClick={() => toggleFaq(i)}
                    className="w-full flex items-start justify-between gap-4 px-3 py-5 text-left cursor-pointer group"
                  >
                    <span
                      className={`text-sm md:text-base font-medium transition-colors duration-300 ${
                        isOpen ? 'text-foreground-950' : 'text-foreground-800 group-hover:text-foreground-950'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full transition-all duration-400 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                        isOpen
                          ? 'rotate-180 bg-foreground-950 text-background-50'
                          : 'text-foreground-300 bg-background-100 group-hover:text-foreground-500'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-base" />
                    </span>
                  </button>

                  {/* Answer — smooth expand/collapse */}
                  <div
                    className={`grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isOpen
                        ? 'grid-rows-[1fr] opacity-100 translate-y-0'
                        : 'grid-rows-[0fr] opacity-0 -translate-y-1'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-3 pb-6 pt-1">
                        <div className="text-sm text-foreground-500 leading-relaxed whitespace-pre-line">
                          {item.a}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<HelpCategory>('about');
  const [faqContext, setFaqContext] = useState<{ title: string; items: FaqItem[] } | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://settlespace.in';

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: `${siteUrl}/help-center`,
      mainEntity: [...aboutFaqItems, ...ownerFaqItems, ...tenantFaqItems].map(
        (item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        }),
      ),
    }),
    [siteUrl],
  );

  const handleCardClick = (key: HelpCategory) => {
    setSelected(key);
    if (key === 'about') {
      setFaqContext({ title: 'Questions asked About Us', items: aboutFaqItems });
    } else if (key === 'owner') {
      setFaqContext({ title: 'Questions asked by Owners', items: ownerFaqItems });
    } else if (key === 'tenant') {
      setFaqContext({ title: 'Questions asked by Tenants', items: tenantFaqItems });
    }
  };

  const aboutCard = helpCards[0]; // About Us
  const bottomCards = helpCards.slice(1); // Tenant, Owner

  const renderCard = (card: typeof helpCards[0]) => {
    const isSelected = selected === card.key;

    return (
      <button
        key={card.key}
        type="button"
        onClick={() => handleCardClick(card.key)}
        className={`relative flex flex-col items-start gap-4 p-6 md:gap-5 md:p-7 rounded-[32px] md:rounded-[40px] text-left cursor-pointer transition-all duration-400 ease-[cubic-bezier(0.34,1.56,0.64,1)] group hover:-translate-y-2 hover:scale-[1.04] ${
          isSelected
            ? 'bg-primary-100/60'
            : 'bg-[#EEEDEA]'
        }`}
      >
        {/* Icon — bare line icon, no background box */}
        <span className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center transition-colors duration-300 ${
          isSelected
            ? 'text-primary-600'
            : 'text-foreground-400 group-hover:text-foreground-600'
        }`}>
          <i className={`${card.icon} text-xl md:text-xl`} />
        </span>

        {/* Text */}
        <div className="flex flex-col items-start gap-1 pb-3 md:pb-6">
          <span className={`text-[15px] md:text-base font-semibold transition-colors duration-200 ${
            isSelected ? 'text-primary-800' : 'text-foreground-950'
          }`}>
            {card.label}
          </span>
          <span className="text-[13px] md:text-sm text-foreground-400 leading-relaxed">
            {card.description}
          </span>
        </div>

        {/* Chevron — bottom-right */}
        <span className={`absolute right-4 bottom-4 md:right-5 md:bottom-5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center transition-all duration-300 ${
          isSelected
            ? 'text-primary-500 translate-x-1'
            : 'text-foreground-300 group-hover:text-foreground-500 group-hover:translate-x-1'
        }`}>
          <i className="ri-arrow-right-s-line text-lg" />
        </span>
      </button>
    );
  };

  return (
    <>
      <JsonLd data={faqJsonLd} />
      <div className="bg-background-50">
      {/* Back to Home — pulled well below the navbar */}
      <div className="w-full max-w-3xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28">
        <button
          type="button"
          onClick={() => {
            navigate('/');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                document.getElementById('site-footer')?.scrollIntoView({ behavior: 'auto' });
              });
            });
          }}
          className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer group"
        >
          <span className="w-5 h-5 flex items-center justify-center transition-transform duration-200 group-hover:-translate-x-0.5">
            <i className="ri-arrow-left-line text-base" />
          </span>
          <span>Back to Home</span>
        </button>
      </div>

      {/* Heading + content */}
      <div className="w-full max-w-3xl mx-auto px-6 md:px-12 lg:px-16 pt-4 md:pt-10 pb-12 md:pb-24">
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
          How can we Assist you?
        </h1>
        <p className="text-sm text-foreground-600 mb-6 md:mb-10">
          Choose an option below.
        </p>

        {/* Cards layout — About Us centered on top, Tenant & Owner side by side below */}
        <div className="flex flex-col items-center gap-4 md:gap-5">
          {/* Top row: About Us — centered, slightly wider */}
          <div className="w-full max-w-sm">
            {renderCard(aboutCard)}
          </div>

          {/* Bottom row: Tenant + Owner — side by side */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-6 w-full max-w-sm sm:max-w-lg">
            {bottomCards.map((card) => (
              <div key={card.key} className="flex-1">
                {renderCard(card)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Modal — rendered persistently so open/close transitions stay smooth */}
      <FaqModal
        open={faqContext !== null}
        onClose={() => setFaqContext(null)}
        title={faqContext?.title ?? ''}
        items={faqContext?.items ?? []}
      />
      </div>
    </>
  );
}